import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
} from '@mui/material';
import { Settings, Sun, Moon, ChevronLeft, ChevronRight, UserCircle, LogOut } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { AdminAgentToggleButton } from '../../features/admin/admin-agent/components/AdminAgentToggleButton';
import { User } from '../../types';
import {
  codeAgentConfigNavItems,
  codeAgentMonitorNavItems,
  logsNavItems,
  mainNavItems,
  settingsNavItems,
  systemNavItems,
  type NavItem,
} from './sidebarNavItems';

interface SidebarProps {
  user: User;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

interface NavSectionProps {
  title: string;
  items: NavItem[];
  isCollapsed: boolean;
  isActive: (path: string) => boolean;
}

const NavSection: React.FC<NavSectionProps> = ({ title, items, isCollapsed, isActive }) => (
  <List className="px-3">
    {!isCollapsed && (
      <Typography
        variant="caption"
        className="px-4 py-2 font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400"
      >
        {title}
      </Typography>
    )}
    {items.map(({ id, label, path, Icon }) => {
      const active = isActive(path);

      return (
        <ListItem key={id} disablePadding className="mb-1">
          <Tooltip title={isCollapsed ? label : ''} placement="right" arrow>
            <ListItemButton
              component={Link}
              to={path}
              className={`rounded-lg transition-all ${
                active
                  ? 'bg-blue-500/10 text-blue-500 dark:bg-blue-500/20 dark:text-blue-400'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-700'
              } ${isCollapsed ? 'justify-center' : ''}`}
              sx={{
                py: 1.5,
                px: isCollapsed ? 1 : 2,
              }}
            >
              <ListItemIcon
                className={
                  active ? 'text-blue-500 dark:text-blue-400' : 'text-gray-500 dark:text-slate-400'
                }
                sx={{ minWidth: isCollapsed ? 'auto' : 40 }}
              >
                <Icon size={20} />
              </ListItemIcon>
              {!isCollapsed && (
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: active ? 600 : 500,
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      );
    })}
  </List>
);

export const Sidebar: React.FC<SidebarProps> = ({ user, isCollapsed, onToggleCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { mode, toggleTheme } = useTheme();
  const { logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePersonalDetail = () => {
    handleMenuClose();
    // TODO: Navigate to personal detail page or open modal
    console.log('Navigate to personal detail');
  };

  const handleUserSettings = () => {
    handleMenuClose();
    navigate('/account-settings');
  };

  const handleLogout = async () => {
    handleMenuClose();
    try {
      // Call logout function from AuthContext
      // This will: 1) call POST /auth/logout, 2) clear local state, 3) redirect to "/"
      await logout();
    } catch (error) {
      // Error is already handled in AuthContext logout function
      console.error('Logout handler error:', error);
    }
  };

  return (
    <Box
      className={`fixed left-0 top-0 flex h-screen flex-col border-r border-gray-200 bg-white transition-all duration-300 dark:border-slate-700 dark:bg-slate-800 ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
    >
      <Box
        className={`flex items-center border-b border-gray-200 dark:border-slate-700 ${
          isCollapsed ? 'flex-col justify-center gap-1 px-2 py-3' : 'justify-between px-6 py-4'
        }`}
      >
        {!isCollapsed && (
          <Link to="/dashboard" className="flex items-center gap-2 no-underline">
            <img src="/logo.png" alt="" className="h-8 w-8 rounded-lg" />
            <Typography
              variant="h6"
              className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text font-bold text-transparent dark:from-blue-400 dark:to-cyan-400"
            >
              AppAI
            </Typography>
          </Link>
        )}
        <Box className="flex items-center gap-1">
          <AdminAgentToggleButton />
          <Tooltip title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
            <IconButton
              onClick={onToggleCollapse}
              size="small"
              className="text-gray-600 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-700"
            >
              {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box className="flex-1 overflow-y-auto py-4">
        <NavSection
          title="Main"
          items={mainNavItems}
          isCollapsed={isCollapsed}
          isActive={isActive}
        />
        <Divider className="mx-3 my-4" />

        <NavSection
          title="System"
          items={systemNavItems}
          isCollapsed={isCollapsed}
          isActive={isActive}
        />
        <Divider className="mx-3 my-4" />

        <NavSection
          title="Logs"
          items={logsNavItems}
          isCollapsed={isCollapsed}
          isActive={isActive}
        />
        <Divider className="mx-3 my-4" />

        <NavSection
          title="Code Agent"
          items={codeAgentMonitorNavItems}
          isCollapsed={isCollapsed}
          isActive={isActive}
        />
        <NavSection
          title="Code Agent Config"
          items={codeAgentConfigNavItems}
          isCollapsed={isCollapsed}
          isActive={isActive}
        />
        <Divider className="mx-3 my-4" />

        <NavSection
          title="Settings"
          items={settingsNavItems}
          isCollapsed={isCollapsed}
          isActive={isActive}
        />
      </Box>

      <Box className="border-t border-gray-200 dark:border-slate-700">
        <Box
          className={`flex items-center py-3 ${isCollapsed ? 'justify-center px-2' : 'justify-between px-6'}`}
        >
          {!isCollapsed && (
            <Typography variant="body2" className="font-medium text-gray-700 dark:text-slate-300">
              {mode === 'light' ? 'Light Mode' : 'Dark Mode'}
            </Typography>
          )}
          <Tooltip title={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`}>
            <IconButton
              onClick={toggleTheme}
              className="text-gray-600 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-700"
              size="small"
            >
              {mode === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          className={`cursor-pointer bg-gray-50 py-4 transition-colors hover:bg-gray-100 dark:bg-slate-900/50 dark:hover:bg-slate-900/70 ${isCollapsed ? 'px-2' : 'px-4'}`}
          onClick={handleProfileClick}
        >
          {isCollapsed ? (
            <Tooltip
              title={`${user.userName} (${user.subscription || 'free'})`}
              placement="right"
              arrow
            >
              <Box className="flex justify-center">
                <Avatar
                  src={user.avatar}
                  alt={user.userName}
                  className="bg-gradient-to-br from-indigo-500 to-pink-500"
                  sx={{ width: 32, height: 32 }}
                >
                  {user.userName.charAt(0).toUpperCase()}
                </Avatar>
              </Box>
            </Tooltip>
          ) : (
            <>
              <Box className="flex items-center gap-3">
                <Avatar
                  src={user.avatar}
                  alt={user.userName}
                  className="bg-gradient-to-br from-indigo-500 to-pink-500"
                  sx={{ width: 40, height: 40 }}
                >
                  {user.userName.charAt(0).toUpperCase()}
                </Avatar>
                <Box className="min-w-0 flex-1">
                  <Typography
                    variant="body2"
                    className="truncate font-semibold text-gray-900 dark:text-slate-100"
                  >
                    {user.userName}
                  </Typography>
                  <Typography
                    variant="caption"
                    className="block truncate text-gray-500 dark:text-slate-400"
                  >
                    {user.email}
                  </Typography>
                </Box>
              </Box>
              <Box className="mt-2">
                <Typography
                  variant="caption"
                  className="inline-block rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                >
                  Super Admin
                </Typography>
              </Box>
            </>
          )}
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: isCollapsed ? 'left' : 'right',
          }}
          slotProps={{
            paper: {
              className: 'mt-2 min-w-[200px] rounded-lg shadow-lg',
            },
          }}
        >
          <MenuItem
            onClick={handlePersonalDetail}
            className="gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            <UserCircle size={18} className="text-gray-500 dark:text-slate-400" />
            <Typography variant="body2">Personal Detail</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleUserSettings}
            className="gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            <Settings size={18} className="text-gray-500 dark:text-slate-400" />
            <Typography variant="body2">User Settings</Typography>
          </MenuItem>
          <Divider className="my-1" />
          <MenuItem
            onClick={handleLogout}
            className="gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
          >
            <LogOut size={18} />
            <Typography variant="body2">Logout</Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};
